import { useMemo } from 'react';
import { useEditorStore } from '@/stores/editor-store';
import { BEAD_DIMENSIONS } from '@bransoletki/shared';

const TECHNIQUE_LABELS: Record<string, string> = {
  loom: 'Loom',
  peyote: 'Peyote',
  brick: 'Brick Stitch',
};

export function BeadStats() {
  const grid = useEditorStore((s) => s.grid);
  const palette = useEditorStore((s) => s.palette);
  const gridWidth = useEditorStore((s) => s.gridWidth);
  const gridHeight = useEditorStore((s) => s.gridHeight);
  const beadSize = useEditorStore((s) => s.beadSize);
  const technique = useEditorStore((s) => s.technique);
  const projectName = useEditorStore((s) => s.projectName);

  const stats = useMemo(() => {
    // Count beads per palette index
    const counts = new Map<number, number>();
    let totalFilled = 0;

    for (const row of grid) {
      for (const cell of row) {
        if (cell > 0) {
          totalFilled++;
          counts.set(cell, (counts.get(cell) ?? 0) + 1);
        }
      }
    }

    // Physical dimensions
    const dims = BEAD_DIMENSIONS[beadSize] ?? [1.6, 1.3];
    const beadW = dims[0];
    const beadH = dims[1];

    let physicalWidth: number;
    let physicalHeight: number;

    switch (technique) {
      case 'loom':
        physicalWidth = gridWidth * beadW;
        physicalHeight = gridHeight * beadH;
        break;
      case 'peyote':
        physicalWidth = gridWidth * beadW + beadW / 2;
        physicalHeight = gridHeight * (beadH / 2) + beadH / 2;
        break;
      case 'brick':
        physicalWidth = gridWidth * beadW + beadW / 2;
        physicalHeight = gridHeight * beadH;
        break;
    }

    // mm → inches
    const widthInches = physicalWidth / 25.4;
    const heightInches = physicalHeight / 25.4;

    // Cost estimation: ~1100 beads per tube, ~8-15 PLN per tube
    const BEADS_PER_TUBE = 1100;
    const COST_PER_TUBE_PLN = 12;
    const uniqueColors = counts.size;
    const tubesNeeded = Math.max(uniqueColors, Math.ceil(totalFilled / BEADS_PER_TUBE));
    const estimatedCostPln = tubesNeeded * COST_PER_TUBE_PLN;

    return {
      totalFilled,
      totalCells: gridWidth * gridHeight,
      counts,
      physicalWidth: Math.round(physicalWidth * 10) / 10,
      physicalHeight: Math.round(physicalHeight * 10) / 10,
      widthInches: Math.round(widthInches * 100) / 100,
      heightInches: Math.round(heightInches * 100) / 100,
      tubesNeeded,
      estimatedCostPln,
    };
  }, [grid, gridWidth, gridHeight, beadSize, technique]);

  return (
    <div className="flex flex-col gap-2 p-3">
      {/* Project info header */}
      <div className="rounded-lg bg-primary-50 px-3 py-2">
        <div className="text-sm font-semibold text-gray-900">{projectName}</div>
        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-600">
          <div>Technika: <span className="font-medium">{TECHNIQUE_LABELS[technique] ?? technique}</span></div>
          <div>Koralik: <span className="font-medium">{beadSize}</span></div>
          <div>Szerokość: <span className="font-medium">{gridWidth}</span></div>
          <div>Wysokość: <span className="font-medium">{gridHeight}</span></div>
        </div>
      </div>

      {/* Physical dimensions */}
      <div className="rounded-lg bg-gray-50 px-3 py-2">
        <div className="text-[10px] font-medium text-gray-500">Wymiary gotowej pracy</div>
        <div className="text-sm font-semibold text-gray-800">
          {stats.physicalWidth} × {stats.physicalHeight} mm
        </div>
        <div className="text-xs text-gray-400">
          {stats.widthInches} × {stats.heightInches}&quot;
        </div>
      </div>

      {/* Total beads + cost */}
      <div className="rounded-lg bg-gray-50 px-3 py-2">
        <div className="text-[10px] font-medium text-gray-500">Koraliki</div>
        <div className="text-sm font-semibold text-gray-800">
          {stats.totalFilled} / {stats.totalCells}
        </div>
        <div className="text-xs text-gray-400">
          ~{stats.tubesNeeded} tubek ({stats.estimatedCostPln} zł)
        </div>
      </div>

      {/* Per-color breakdown */}
      {palette.length > 0 && (
        <div className="rounded-lg bg-gray-50 px-3 py-2">
          <div className="mb-1.5 text-[10px] font-medium text-gray-500">
            {palette.length} {palette.length === 1 ? 'kolor' : palette.length < 5 ? 'kolory' : 'kolorów'}
          </div>
          <div className="flex flex-col gap-1.5">
            {palette.map((color) => {
              const count = stats.counts.get(color.index) ?? 0;
              return (
                <div key={color.index} className="flex items-center gap-2">
                  <div
                    className="h-5 w-5 shrink-0 rounded border border-gray-200"
                    style={{ backgroundColor: color.beadHex }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-gray-700">
                      {color.beadCode || color.label}
                    </div>
                    <div className="text-[10px] text-gray-400">{color.beadHex}</div>
                  </div>
                  <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
